<template>
  <el-dialog
    :visible.sync="dialogVisible"
    width="400px"
    :show-close="false"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    custom-class="trial-notice-dialog"
    append-to-body
    @closed="onClosed"
  >
    <div class="trial-notice" :class="`trial-notice--${mode}`">
      <div class="trial-notice__icon" :class="`trial-notice__icon--${mode}`">
        <i v-if="mode === 'register'" class="el-icon-check"></i>
        <i v-else-if="mode === 'expire'" class="el-icon-warning-outline"></i>
        <i v-else class="el-icon-circle-close"></i>
      </div>
      <h3 class="trial-notice__title">{{ displayTitle }}</h3>
      <p class="trial-notice__content" v-html="displayContentHtml"></p>
      <div v-if="displayQrUrl" class="trial-notice__qr-wrap">
        <img :src="displayQrUrl" alt="客服二维码" class="trial-notice__qr" />
        <p v-if="displayQrTip" class="trial-notice__qr-tip">{{ displayQrTip }}</p>
      </div>
      <el-button type="primary" class="trial-notice__btn" @click="handleConfirm">
        我知道了
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import config from '@/config/config'
import { formatTrialContent, resolveImageUrl } from '@/utils/trial'

export default {
  name: 'TrialNoticeDialog',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'register',
      validator: (val) => ['register', 'expire', 'expired'].includes(val),
    },
    config: {
      type: Object,
      default: () => ({}),
    },
    remainDays: {
      type: [Number, String],
      default: null,
    },
    override: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      },
    },
    displayTitle() {
      if (this.override.title) return this.override.title
      if (this.mode === 'register') {
        return this.config.register_success_title || '注册成功'
      }
      if (this.mode === 'expire') {
        return this.config.expire_remind_title || '试用期即将到期'
      }
      return this.config.expired_login_title || '试用期已到期'
    },
    displayContent() {
      if (this.override.content) return this.override.content
      if (this.mode === 'register') {
        return formatTrialContent(this.config.register_success_content, {
          trial_days: this.config.trial_days,
        })
      }
      if (this.mode === 'expire') {
        return formatTrialContent(this.config.expire_remind_content, {
          remain_days: this.remainDays,
        })
      }
      return this.config.expired_login_tip || '您的试用期已到期，请联系客服续期。'
    },
    displayContentHtml() {
      const text = this.displayContent
      if (this.mode === 'expire') {
        return text.replace(
          /(\d+)\s*天/g,
          '<span class="trial-notice__highlight">$1 天</span>'
        )
      }
      if (this.mode === 'register') {
        return text.replace(
          /(\d+)\s*天/g,
          '<span class="trial-notice__highlight trial-notice__highlight--green">$1 天</span>'
        )
      }
      return text
    },
    displayQrUrl() {
      const path =
        this.override.qr ||
        (this.mode === 'register'
          ? this.config.register_success_qr
          : this.mode === 'expire'
          ? this.config.expire_remind_qr
          : this.config.expired_login_qr || this.config.expire_remind_qr)
      return resolveImageUrl(path, config.config)
    },
    displayQrTip() {
      if (this.override.qr_tip !== undefined) return this.override.qr_tip
      if (this.mode === 'register') return this.config.register_success_qr_tip
      if (this.mode === 'expire') return this.config.expire_remind_qr_tip
      return this.config.expired_login_qr_tip || this.config.expire_remind_qr_tip
    },
  },
  watch: {
    dialogVisible(val) {
      document.body.classList.toggle('trial-notice-open', val)
    },
  },
  beforeDestroy() {
    document.body.classList.remove('trial-notice-open')
  },
  methods: {
    handleConfirm() {
      this.$emit('confirm')
      this.dialogVisible = false
    },
    onClosed() {
      document.body.classList.remove('trial-notice-open')
      this.$emit('closed')
    },
  },
}
</script>

<style lang="scss">
.trial-notice-dialog.el-dialog {
  border-radius: 20px;
  overflow: hidden;
  border: none;
  background: #fff;
  box-shadow: 0 12px 40px rgba(15, 23, 42, 0.12);

  .el-dialog__header {
    display: none;
  }

  .el-dialog__body {
    padding: 0;
    border-radius: 20px;
  }
}

.trial-notice__highlight {
  color: #f97316;
  font-weight: 600;
}

.trial-notice__highlight--green {
  color: #22c55e;
}

body.trial-notice-open .v-modal {
  background: rgba(0, 0, 0, 0.45) !important;
}
</style>

<style lang="scss" scoped>
.trial-notice {
  text-align: center;
  padding: 40px 32px 32px;
  background: #fff;
  border-radius: 20px;
}

.trial-notice__icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
}

.trial-notice__icon--register {
  background: #ecfdf5;
  color: #22c55e;
}

.trial-notice__icon--expire {
  background: #fff7ed;
  color: #f97316;
}

.trial-notice__icon--expired {
  background: #fef2f2;
  color: #ef4444;
}

.trial-notice__title {
  margin: 0 0 12px;
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.4;
}

.trial-notice__content {
  margin: 0 auto 24px;
  max-width: 300px;
  font-size: 14px;
  line-height: 1.7;
  color: #64748b;
}

.trial-notice__qr-wrap {
  margin-bottom: 24px;
}

.trial-notice__qr {
  width: 160px;
  height: 160px;
  object-fit: contain;
  border-radius: 12px;
  border: 1px solid #e8eef5;
  padding: 8px;
  background: #fff;
}

.trial-notice__qr-tip {
  margin: 10px 0 0;
  font-size: 13px;
  color: #94a3b8;
}

.trial-notice__btn {
  width: 100%;
  max-width: 280px;
  height: 44px;
  border: none;
  border-radius: 22px;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.08em;
  padding-left: 0.08em;
  background: #0092ff;
  box-shadow: none;
}

::v-deep .trial-notice__btn:hover,
::v-deep .trial-notice__btn:focus {
  background: #007dd9;
}
</style>
